import { useCallback, useEffect, useMemo } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import toast from "react-hot-toast";
import { Check, CheckCircle, RotateCcw } from "lucide-react";

import { EmptyState } from "@/components/shared/EmptyState";
import { LoadingLogo } from "@/components/shared/LoadingLogo";
import { PageHeader } from "@/components/shared/PageHeader";
import { TableCard } from "@/components/shared/TableCard";
import { TablePagination } from "@/components/shared/TablePagination";
import {
  TableActionsMenu,
  type TableAction,
} from "@/components/shared/TableActionsMenu";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { usePaginationFromUrl } from "@/hooks/usePaginationFromUrl";
import { formatDisplayDate } from "@/lib/dateFormat";
import {
  getFeedbackList,
  updateFeedbackStatus,
  type FeedbackListItem,
  type FeedbackStatus,
} from "@/services/feedbackService";

const FEEDBACK_QUERY_KEY = ["platform", "feedback"] as const;

const MESSAGE_TRUNCATE_LEN = 80;

function truncateMessage(msg: string, maxLen: number): string {
  if (msg.length <= maxLen) return msg;
  return msg.slice(0, maxLen) + "…";
}

export function FeedbackPage(): JSX.Element {
  const { t, i18n } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const pagination = usePaginationFromUrl();
  const queryClient = useQueryClient();

  const statusParam = searchParams.get("status") ?? "all";
  const statusFilter: FeedbackStatus | undefined =
    statusParam === "new" || statusParam === "read" || statusParam === "resolved"
      ? statusParam
      : undefined;

  const setStatusFilter = useCallback(
    (value: string) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          if (value === "all") next.delete("status");
          else next.set("status", value);
          next.set("page", "1");
          return next;
        },
        { replace: true }
      );
    },
    [setSearchParams]
  );

  const listParams = useMemo(
    () => ({
      page: pagination.page,
      limit: pagination.limit,
      status: statusFilter,
    }),
    [pagination.page, pagination.limit, statusFilter]
  );

  const {
    data,
    isLoading,
    isFetching,
    isError,
    refetch,
  } = useQuery({
    queryKey: [...FEEDBACK_QUERY_KEY, listParams],
    queryFn: () =>
      getFeedbackList(listParams.page, listParams.limit, listParams.status),
  });

  const updateStatusMutation = useMutation({
    mutationFn: ({ id, status }: { id: string; status: FeedbackStatus }) =>
      updateFeedbackStatus(id, status),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: FEEDBACK_QUERY_KEY });
    },
    onError: () => {
      toast.error(t("feedback.page.statusUpdateError"));
    },
  });

  useEffect(() => {
    if (data != null) {
      pagination.setTotal(data.total);
    } else if (isError) {
      pagination.setTotal(0);
    }
  }, [data, isError, pagination]);

  useEffect(() => {
    if (isError) {
      toast.error(t("feedback.page.error"));
    }
  }, [isError, t]);

  const items: FeedbackListItem[] = data?.data ?? [];
  const loading = isLoading;

  return (
    <div className="space-y-4 fx-content">
      <PageHeader
        title={t("feedback.page.title")}
        description={t("feedback.page.description")}
      />
      <div className="flex flex-wrap items-center gap-2">
        <Select
          value={statusParam}
          onValueChange={setStatusFilter}
        >
          <SelectTrigger className="w-[160px]">
            <SelectValue placeholder={t("feedback.page.filterStatus")} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t("feedback.page.filterAll")}</SelectItem>
            <SelectItem value="new">{t("feedback.page.statusNew")}</SelectItem>
            <SelectItem value="read">{t("feedback.page.statusRead")}</SelectItem>
            <SelectItem value="resolved">
              {t("feedback.page.statusResolved")}
            </SelectItem>
          </SelectContent>
        </Select>
      </div>
      <TableCard>
        {loading ? (
          <div className="flex min-h-[40vh] items-center justify-center py-12">
            <LoadingLogo size="lg" />
          </div>
        ) : items.length === 0 ? (
          <EmptyState
            title={t("feedback.page.emptyTitle")}
            message={t("feedback.page.emptyMessage")}
          />
        ) : (
          <>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t("feedback.page.tenant")}</TableHead>
                  <TableHead>{t("feedback.page.user")}</TableHead>
                  <TableHead>{t("feedback.page.message")}</TableHead>
                  <TableHead>{t("feedback.page.date")}</TableHead>
                  <TableHead>{t("feedback.page.status")}</TableHead>
                  <TableHead className="w-[60px]" aria-hidden />
                </TableRow>
              </TableHeader>
              <TableBody>
                {items.map((row) => {
                  const actions: TableAction[] = [];
                  if (row.status === "new") {
                    actions.push({
                      label: t("feedback.page.markRead"),
                      icon: Check,
                      onClick: () =>
                        updateStatusMutation.mutate({
                          id: row.id,
                          status: "read",
                        }),
                    });
                  }
                  if (row.status === "new" || row.status === "read") {
                    actions.push({
                      label: t("feedback.page.markResolved"),
                      icon: CheckCircle,
                      onClick: () =>
                        updateStatusMutation.mutate({
                          id: row.id,
                          status: "resolved",
                        }),
                    });
                  }
                  if (row.status !== "new") {
                    actions.push({
                      label: t("feedback.page.markNew"),
                      icon: RotateCcw,
                      onClick: () =>
                        updateStatusMutation.mutate({
                          id: row.id,
                          status: "new",
                        }),
                    });
                  }
                  const truncated = truncateMessage(row.message, MESSAGE_TRUNCATE_LEN);
                  const showTooltip = row.message.length > MESSAGE_TRUNCATE_LEN;
                  const statusLabel =
                    row.status === "new"
                      ? t("feedback.page.statusNew")
                      : row.status === "read"
                        ? t("feedback.page.statusRead")
                        : t("feedback.page.statusResolved");
                  return (
                    <TableRow key={row.id}>
                      <TableCell className="font-medium">
                        {row.tenantName || "—"}
                      </TableCell>
                      <TableCell className="text-muted-foreground">
                        <span className="block truncate" title={row.userEmail}>
                          {row.userName?.trim() || row.userEmail}
                        </span>
                        {row.userName?.trim() && (
                          <span className="block truncate text-xs text-muted-foreground">
                            {row.userEmail}
                          </span>
                        )}
                      </TableCell>
                      <TableCell className="max-w-[280px]">
                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <span className="block truncate">
                                {truncated}
                              </span>
                            </TooltipTrigger>
                            {showTooltip && (
                              <TooltipContent
                                side="top"
                                className="max-w-sm whitespace-pre-wrap break-words"
                              >
                                {row.message}
                              </TooltipContent>
                            )}
                          </Tooltip>
                        </TooltipProvider>
                      </TableCell>
                      <TableCell className="text-muted-foreground whitespace-nowrap">
                        {formatDisplayDate(row.createdAt, { includeTime: true, locale: i18n.language })}
                      </TableCell>
                      <TableCell>
                        <span
                          className={
                            row.status === "new"
                              ? "font-medium text-foreground"
                              : "text-muted-foreground"
                          }
                        >
                          {statusLabel}
                        </span>
                      </TableCell>
                      <TableCell onClick={(e) => e.stopPropagation()}>
                        {actions.length > 0 ? (
                          <TableActionsMenu actions={actions} />
                        ) : null}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
            <TablePagination
              pagination={pagination}
              rowsPerPageLabel={t("feedback.page.rowsPerPage")}
              pageOfLabel={t("feedback.page.pageOf", {
                from: (pagination.page - 1) * pagination.limit + 1,
                to: Math.min(
                  pagination.page * pagination.limit,
                  pagination.total
                ),
                total: pagination.total,
              })}
              onRefresh={() => void refetch()}
              isRefreshing={isFetching}
            />
          </>
        )}
      </TableCard>
    </div>
  );
}
